import React from "react";
import { Slider } from "@/components/ui/slider";
import { Minus, Plus, TriangleAlert } from "lucide-react";

/**
 * Money / percent / count / phone fields used across the Hub and both partner portals.
 * No native `type="number"` anywhere — spinner arrows are gone, commas are typeable,
 * and every figure can be dragged instead of typed.
 */

const buildLadder = (bands) => {
  const out = [];
  let from = 0;
  for (const [to, step] of bands) {
    for (let a = from; a < to; a += step) out.push(a);
    from = to;
  }
  out.push(from);
  return out;
};

export const LADDERS = {
  deal:     buildLadder([[100000, 5000], [1000000, 25000], [10000000, 250000], [100000000, 2500000], [500000000, 25000000]]),
  revenue:  buildLadder([[100000, 5000], [1000000, 25000], [10000000, 250000], [100000000, 2500000], [500000000, 25000000]]),
  fee:      buildLadder([[10000, 250], [100000, 2500], [1000000, 25000]]),
  capacity: buildLadder([[1000000, 50000], [10000000, 250000], [100000000, 2500000], [1000000000, 25000000]]),
};

export const DEAL_PRESETS = [100000, 500000, 1000000, 5000000, 10000000, 25000000, 50000000, 100000000, 250000000, 500000000];

export function formatShortGbp(a) {
  const n = Number(a) || 0;
  const trim = (v) => String(parseFloat(v.toFixed(2)));
  if (!n) return "£0";
  if (n >= 1e9) return `£${trim(n / 1e9)}bn`;
  if (n >= 1e6) return `£${trim(n / 1e6)}m`;
  if (n >= 1e3) return `£${Math.round(n / 1e3)}k`;
  return `£${n}`;
}

const digits = (s) => String(s ?? "").replace(/[^0-9]/g, "");

const nearestIndex = (ladder, n) => {
  if (n >= ladder[ladder.length - 1]) return ladder.length - 1;
  let best = 0;
  for (let i = 0; i < ladder.length; i++) {
    if (Math.abs(ladder[i] - n) < Math.abs(ladder[best] - n)) best = i;
  }
  return best;
};

export function MoneyField({ value, onChange, ladder = "deal", presets, testId, placeholder = "0",
                            dark = false, compact = false, hint, maxLabel }) {
  const steps = LADDERS[ladder] || LADDERS.deal;
  const top = steps[steps.length - 1];
  const n = parseInt(digits(value), 10) || 0;
  const over = n > top;

  return (
    <div className={`rounded-xl border p-3 ${dark ? "border-white/10 bg-white/[0.04]" : "border-slate-200 bg-slate-50/60"}`}>
      <div className="flex items-center gap-1.5">
        <span className={`font-semibold ${compact ? "text-[16px]" : "text-[22px]"} ${dark ? "text-white/40" : "text-slate-400"}`}>£</span>
        <input
          inputMode="numeric"
          value={n ? n.toLocaleString() : ""}
          placeholder={placeholder}
          onChange={(e) => { const d = digits(e.target.value); onChange(d ? String(parseInt(d, 10)) : ""); }}
          className={`w-full bg-transparent outline-none font-semibold tabular-nums ${compact ? "text-[17px]" : "text-[24px]"} ${dark ? "text-white placeholder:text-slate-600" : "text-slate-900 placeholder:text-slate-300"}`}
          data-testid={testId}
        />
        {n > 0 && (
          <span className="shrink-0 rounded-full border border-[#0055FF]/25 bg-[#0055FF]/[0.1] px-2 h-6 inline-flex items-center text-[11.5px] font-semibold text-[#0055FF] tabular-nums"
                data-testid={testId ? `${testId}-short` : undefined}>
            {formatShortGbp(n)}
          </span>
        )}
      </div>
      <Slider value={[nearestIndex(steps, n)]} min={0} max={steps.length - 1} step={1}
              onValueChange={([i]) => onChange(String(steps[i]))}
              className={compact ? "mt-2.5" : "mt-4"} data-testid={testId ? `${testId}-slider` : undefined} />
      <div className={`mt-1 flex items-center justify-between text-[10.5px] tabular-nums ${dark ? "text-white/35" : "text-slate-400"}`}>
        <span>{hint || "drag or type — no limit"}</span>
        <span>{over ? `${formatShortGbp(n)} — above slider` : (maxLabel || `${formatShortGbp(top)}+`)}</span>
      </div>
      {presets?.length ? (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {presets.map((a) => (
            <button key={a} type="button" onClick={() => onChange(String(a))}
                    className={`rounded-full border px-2.5 h-7 text-[11.5px] font-semibold transition-colors ${
                      n === a ? "border-[#0055FF] bg-[#0055FF]/10 text-[#0055FF]"
                              : dark ? "border-white/15 bg-white/[0.04] text-white/70 hover:border-white/30"
                                     : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                    data-testid={testId ? `${testId}-chip-${a}` : undefined}>
              {formatShortGbp(a)}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function PercentField({ value, onChange, max = 100, step = 0.1, testId, dark = false, hint,
                               warnAbove = 25, presets }) {
  const n = parseFloat(String(value)) || 0;
  const sliderMax = Math.max(max, Math.ceil(n));
  return (
    <div className={`rounded-xl border p-3 ${dark ? "border-white/10 bg-white/[0.04]" : "border-slate-200 bg-slate-50/60"}`}>
      <div className="flex items-baseline gap-1">
        <input
          inputMode="decimal"
          value={value ?? ""}
          placeholder="0"
          onChange={(e) => {
            const clean = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
            onChange(clean);
          }}
          className={`w-20 bg-transparent outline-none text-[19px] font-semibold tabular-nums ${dark ? "text-white placeholder:text-slate-600" : "text-slate-900 placeholder:text-slate-300"}`}
          data-testid={testId}
        />
        <span className={`text-[15px] font-semibold ${dark ? "text-white/45" : "text-slate-400"}`}>%</span>
      </div>
      <Slider value={[Math.min(n, sliderMax)]} min={0} max={sliderMax} step={step}
              onValueChange={([v]) => onChange(String(Number(v.toFixed(2))))}
              className="mt-2.5" data-testid={testId ? `${testId}-slider` : undefined} />
      <div className={`mt-1 flex items-start justify-between gap-4 text-[10.5px] ${dark ? "text-white/35" : "text-slate-400"}`}>
        <span className="leading-snug">{hint || "drag or type"}</span><span className="shrink-0 tabular-nums">{sliderMax}%</span>
      </div>
      {presets?.length ? (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {presets.map((p) => (
            <button key={p} type="button" onClick={() => onChange(String(p))}
                    className={`rounded-full border px-2.5 h-7 text-[11.5px] font-semibold tabular-nums transition-colors ${
                      n === p ? "border-[#0055FF] bg-[#0055FF]/10 text-[#0055FF]"
                              : dark ? "border-white/15 bg-white/[0.04] text-white/70 hover:border-white/30"
                                     : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                    data-testid={testId ? `${testId}-chip-${p}` : undefined}>
              {p}%
            </button>
          ))}
        </div>
      ) : null}
      {n > warnAbove && (
        <div className={`mt-2.5 flex items-start gap-1.5 rounded-lg border px-2 py-1.5 text-[10.5px] leading-snug ${
              dark ? "border-[#7da9ff]/30 bg-[#7da9ff]/[0.10] text-[#a8c6ff]"
                   : "border-[#7da9ff]/45 bg-[#7da9ff]/[0.12] text-[#3B63A8]"}`}
             data-testid={testId ? `${testId}-warn` : undefined}>
          <TriangleAlert className="size-3 shrink-0 mt-px" />
          <span>Unusual rate — {n}% is well above a typical commission. Worth a second look before saving.</span>
        </div>
      )}
    </div>
  );
}

export function CountField({ value, onChange, min = 0, max = 9999, step = 1, suffix, testId, dark = false }) {
  const n = parseInt(digits(value), 10);
  const cur = Number.isFinite(n) ? n : null;
  const bump = (d) => {
    const next = Math.min(max, Math.max(min, (cur ?? 0) + d * step));
    onChange(String(next));
  };
  const btn = `grid place-items-center size-8 rounded-lg border transition-colors ${dark ? "border-white/15 bg-white/[0.04] text-white/70 hover:border-white/35" : "border-slate-200 bg-white text-slate-500 hover:border-slate-400 hover:text-slate-700"}`;
  return (
    <div className="flex items-center gap-1.5">
      <button type="button" onClick={() => bump(-1)} className={btn} data-testid={testId ? `${testId}-minus` : undefined} aria-label="decrease"><Minus className="size-3.5" /></button>
      <input
        inputMode="numeric"
        value={cur ?? ""}
        placeholder="0"
        onChange={(e) => { const d = digits(e.target.value); onChange(d === "" ? "" : String(Math.min(max, parseInt(d, 10)))); }}
        className={`w-20 h-9 rounded-lg border px-2.5 text-center text-[16px] font-semibold tabular-nums outline-none focus:border-[#0055FF] ${dark ? "border-white/10 bg-[#0B1220] text-white placeholder:text-slate-600" : "border-slate-200 bg-white text-slate-900 placeholder:text-slate-300"}`}
        data-testid={testId}
      />
      <button type="button" onClick={() => bump(1)} className={btn} data-testid={testId ? `${testId}-plus` : undefined} aria-label="increase"><Plus className="size-3.5" /></button>
      {suffix && <span className={`text-[12px] ml-0.5 ${dark ? "text-white/45" : "text-slate-500"}`}>{suffix}</span>}
    </div>
  );
}

/** UK-first E.164 normaliser — mirrors backend/sms.py so the UI shows exactly what gets texted. */
export function toE164(raw, defaultCc = "44") {
  const s = String(raw || "").trim();
  if (!s) return null;
  const plus = s.startsWith("+");
  let d = s.replace(/\D+/g, "");
  if (!d) return null;
  if (!plus) {
    if (d.startsWith("00")) d = d.slice(2);
    else if (d.startsWith("0")) d = defaultCc + d.slice(1);
    else if (d.length === 10 && d.startsWith("7")) d = defaultCc + d;
  }
  if (d.length < 10 || d.length > 15) return null;
  return `+${d}`;
}

/** Ofcom reserves 07700 900xxx / 07700 90xxxx for drama + testing — these can never receive SMS. */
const RESERVED = /^44770090\d{4}$/;

export function phoneStatus(raw) {
  const e164 = toE164(raw);
  if (!String(raw || "").trim()) return { e164: null, state: "empty" };
  if (!e164) return { e164: null, state: "invalid", note: "That doesn't look like a full mobile number" };
  if (RESERVED.test(e164.slice(1))) return { e164, state: "reserved", note: "Ofcom test range — SMS can't be delivered to this number" };
  return { e164, state: "ok", note: `We'll text ${e164}` };
}

export function PhoneField({ value, onChange, testId, dark = false, placeholder = "07123 456789",
                            emptyHint = "Optional — add a mobile for SMS deal alerts" }) {
  const st = phoneStatus(value);
  const tone = st.state === "ok" ? (dark ? "text-teal-300" : "text-teal-700")
    : st.state === "empty" ? (dark ? "text-white/35" : "text-slate-400")
    : (dark ? "text-[#a8c6ff]" : "text-[#0044CC]");
  return (
    <div>
      <input
        inputMode="tel"
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9+\s()-]/g, ""))}
        className={`w-full h-10 rounded-lg border px-3 text-[14px] outline-none focus:border-[#0055FF] ${dark ? "border-white/10 bg-white/[0.04] text-white placeholder:text-slate-600" : "border-slate-200 bg-white text-slate-900 placeholder:text-slate-300"}`}
        data-testid={testId}
      />
      <div className={`mt-1 text-[10.5px] ${tone}`} data-testid={testId ? `${testId}-hint` : undefined}>
        {st.state === "empty" ? emptyHint : st.note}
      </div>
    </div>
  );
}
